<template>
    <div class="flex min-h-screen bg-gray-50  transition-colors duration-300">
      <!-- Sidebar -->
      <HrSidebar :isOpen="isSidebarOpen" />
  
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
             <header 
        class="fixed top-0 z-50 w-full bg-white shadow-sm transition-all duration-300"
      >
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>
        <div class="max-w-8xl mt-20 mb-4 mx-auto p-6 ">
          <!-- Main Dashboard View -->
          <div v-if="!showDetailedView">
            
            <!-- ✅ 1. Summary Cards (Organization-wide) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div @click="showDetailedView = true" class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#035a76] cursor-pointer hover:bg-gray-50">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="text-sm font-medium text-[#035a76]">Total Records</p>
                    <p class="text-2xl font-bold text-[#035a76] mt-1">{{ totalRecords }}</p>
                  </div>
                  <div class="p-3 rounded-full bg-blue-100 text-[#035a76]">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01m-.01 4h.01" />
                    </svg>
                  </div>
                </div>
                <p class="text-xs text-[#035a76] mt-2">All attendance records this month</p>
              </div>
              <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#fae240]">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="text-sm font-medium text-[#035a76]">Present Today</p>
                    <p class="text-2xl font-bold text-[#035a76] mt-1">{{ presentToday }}</p>
                  </div>
                  <div class="p-3 rounded-full bg-yellow-100 text-[#fae240]">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <p class="text-xs text-[#035a76] mt-2">Employees present today</p>
              </div>
              <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#e8037e]">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="text-sm font-medium text-[#035a76]">Absent Today</p>
                    <p class="text-2xl font-bold text-[#035a76] mt-1">{{ absentToday }}</p>
                  </div>
                  <div class="p-3 rounded-full bg-pink-100 text-[#e8037e]">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                </div>
                <p class="text-xs text-[#035a76] mt-2">Employees absent today</p>
              </div>
              <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#76ba51]">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="text-sm font-medium text-[#035a76]">Late Today</p>
                    <p class="text-2xl font-bold text-[#035a76] mt-1">{{ lateToday }}</p>
                  </div>
                  <div class="p-3 rounded-full bg-green-100 text-[#76ba51]">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p class="text-xs text-[#035a76] mt-2">Employees who arrived late</p>
              </div>
            </div>

            <!-- ✅ 2. Header with Search & Actions -->
            <div class="flex justify-between items-center mb-6">
              <h1 class="text-2xl font-bold text-[#035a76]">Attendance Management</h1>
              <div class="flex space-x-4">
                <div class="relative">
                  <input v-model="employeeSearch" @input="searchEmployees" type="text" placeholder="Search by Staff ID or Name" class="w-full p-3 border border-gray-300 rounded-md focus:ring-[#76ba51] focus:border-[#76ba51]">
                  <ul v-if="searchResults.length > 0 && employeeSearch" class="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <li v-for="employee in searchResults" :key="employee.id" @click="selectEmployee(employee)" class="p-3 hover:bg-gray-100 cursor-pointer">
                      <div class="flex justify-between">
                        <span class="font-medium">{{ employee.name }}</span>
                        <span class="text-gray-500 text-sm">{{ employee.position }}</span>
                      </div>
                      <div class="text-sm text-gray-500">ID: {{ employee.id }}</div>
                    </li>
                  </ul>
                </div>
                <button
  @click="syncEmployeesToBiometric"
  :disabled="syncingBiometric"
  class="inline-flex items-center px-4 py-2 border border-transparent text-sm
         font-medium rounded-md shadow-sm text-white bg-[#e8037e] hover:bg-[#e8037e]/90 disabled:opacity-50"
>
  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
  <span v-if="syncingBiometric">Syncing…</span>
  <span v-else>Sync to Biometric</span>
</button>
<button
  @click="pullBiometricAttendance"
  :disabled="pullingBiometric"
  class="inline-flex items-center px-4 py-2 border border-transparent text-sm
         font-medium rounded-md shadow-sm text-white bg-[#fae240] hover:bg-[#fae240]/90 disabled:opacity-50"
>
  <span v-if="pullingBiometric">Pulling…</span>
  <span v-else>Pull from Device</span>
</button>
                <button @click="openNewAttendanceModal" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90">
                  + New Attendance
                </button>
                <button
                  @click="openBatchModal"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm
                         font-medium rounded-md shadow-sm text-white bg-[#035a76] hover:bg-[#035a76]/90"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none"
                       viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857
                             M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857
                             m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Batch Attendance
                </button>
              </div>
            </div>

            <!-- ✅ 3. DAILY ATTENDANCE (Organization-wide) - DISPLAYS BY DEFAULT, INCLUDES SELECTED EMPLOYEE -->
            <div class="mb-8">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium text-[#035a76]">Daily Attendance</h3>
                <p class="text-sm text-gray-500">Showing all staff records</p>
              </div>

              <!-- Independent Filters for Daily Attendance -->
              <div class="bg-white p-4 rounded-lg border shadow-sm mb-4">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <!-- From Date -->
                  <div>
                    <label class="block text-sm text-gray-600 mb-1">From Date</label>
                    <input v-model="dailyFilterFromDate" type="date" @change="fetchDailyAttendance" class="w-full border rounded px-3 py-2 focus:ring-[#76ba51] focus:border-[#76ba51]">
                  </div>
                  <!-- To Date -->
                  <div>
                    <label class="block text-sm text-gray-600 mb-1">To Date</label>
                    <input v-model="dailyFilterToDate" type="date" @change="fetchDailyAttendance" class="w-full border rounded px-3 py-2 focus:ring-[#76ba51] focus:border-[#76ba51]">
                  </div>
                  <!-- Status Filter -->
                  <div>
                    <label class="block text-sm text-gray-600 mb-1">Status</label>
                    <select v-model="dailyFilterStatus" @change="fetchDailyAttendance" class="w-full border rounded px-3 py-2 focus:ring-[#76ba51] focus:border-[#76ba51]">
                      <option value="all">All Statuses</option>
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                      <option value="late">Late</option>
                    </select>
                  </div>
                  <!-- Department Filter -->
                  <div>
                    <label class="block text-sm text-gray-600 mb-1">Department</label>
                    <select v-model="dailyFilterDepartment" @change="fetchDailyAttendance" class="w-full border rounded px-3 py-2 focus:ring-[#76ba51] focus:border-[#76ba51]">
                      <option value="all">All Departments</option>
                      <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
                    </select>
                  </div>
                  <!-- Reset Button -->
                  <div class="flex items-end">
                    <button @click="resetDailyFilters" class="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded transition">
                      Reset Filters
                    </button>
                  </div>
                </div>
              </div>

              <!-- Daily Attendance Table - Shows ALL employees including selected one -->
              <div class="overflow-x-auto bg-white rounded-lg border">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-In</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-Out</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="record in filteredDailyAttendance" :key="record.id">
                      <td class="px-6 py-4 whitespace-nowrap font-medium">{{ record.employeeName }}</td>
                      <td class="px-6 py-4 whitespace-nowrap">{{ record.employeeId }}</td>
                      <td class="px-6 py-4 whitespace-nowrap">{{ record.department || 'N/A' }}</td>
                      <td class="px-6 py-4 whitespace-nowrap">{{ formatDate(record.date) }}</td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <span class="px-2 py-1 text-xs rounded-full"
                          :class="{
                            'bg-green-100 text-green-800': record.status === 'present',
                            'bg-red-100 text-red-800': record.status === 'absent',
                            'bg-yellow-100 text-yellow-800': record.status === 'late'
                          }">
                          {{ record.status }}
                        </span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">{{ record.checkIn || 'N/A' }}</td>
                      <td class="px-6 py-4 whitespace-nowrap">{{ record.checkOut || 'N/A' }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button @click="viewAttendanceDetails(record)" class="text-[#76ba51] hover:text-[#76ba51]/70 mr-2">View</button>
                        <button v-if="isManager" @click="correctRecord(record)" class="text-[#76ba51] hover:text-[#76ba51]/70">Edit</button>
                      </td>
                    </tr>
                    <tr v-if="filteredDailyAttendance.length === 0">
                      <td colspan="8" class="px-6 py-4 text-center text-sm text-gray-500">
                        No attendance records found for the selected filters
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- ✅ 4. Selected Employee Card (Only shows when employee selected) -->
            <div v-if="selectedEmployee" class="mb-6 bg-gray-50 p-4 rounded-md">
              <div class="flex justify-between items-center mb-3">
                <h3 class="text-lg font-medium text-[#035a76]">Selected Employee</h3>
                <button @click="clearSelectedEmployee" class="text-gray-500 hover:text-red-500 text-xl font-bold">×</button>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div class="bg-white p-3 rounded border"><p class="text-sm text-gray-500">Employee Name</p><p class="font-medium">{{ selectedEmployee.name }}</p></div>
                <div class="bg-white p-3 rounded border"><p class="text-sm text-gray-500">Position</p><p class="font-medium">{{ selectedEmployee.position }}</p></div>
                <div class="bg-white p-3 rounded border"><p class="text-sm text-gray-500">Department</p><p class="font-medium">{{ selectedEmployee.department }}</p></div>
                <div class="bg-white p-3 rounded border"><p class="text-sm text-gray-500">Staff ID</p><p class="font-medium">{{ selectedEmployee.id }}</p></div>
              </div>
            </div>

            <!-- ✅ 5. Employee-Specific Filters (Only shows when employee selected) -->
            <div v-if="selectedEmployee" class="mb-6 bg-white p-4 rounded-lg border shadow-sm">
              <h3 class="text-md font-medium mb-4 text-[#035a76]">Filter {{ selectedEmployee.name }}'s Attendance</h3>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label class="text-sm text-gray-600">From Date</label>
                  <input v-model="filterFromDate" type="date" @change="fetchAttendance" class="w-full border rounded px-3 py-2">
                </div>
                <div>
                  <label class="text-sm text-gray-600">To Date</label>
                  <input v-model="filterToDate" type="date" @change="fetchAttendance" class="w-full border rounded px-3 py-2">
                </div>
                <div>
                  <label class="text-sm text-gray-600">Status</label>
                  <select v-model="filterStatus" @change="fetchAttendance" class="w-full border rounded px-3 py-2">
                    <option value="all">All</option>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="late">Late</option>
                  </select>
                </div>
                <div class="flex items-end">
                  <button @click="resetFilters" class="w-full bg-gray-200 hover:bg-gray-300 px-3 py-2 rounded">Reset</button>
                </div>
              </div>
            </div>

            <!-- ✅ 6. Attendance History for Selected Employee (Only shows when employee selected) -->
            <div v-if="selectedEmployee" class="mb-8">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium text-[#035a76]">Attendance History</h3>
                <p class="text-sm text-gray-500">Records for {{ selectedEmployee.name }}</p>
              </div>
              <div class="overflow-x-auto bg-white rounded-lg border">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-In</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-Out</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="record in attendanceRecords" :key="record.id">
                      <td class="px-6 py-4 whitespace-nowrap">{{ formatDate(record.date) }}</td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <span class="px-2 py-1 text-xs rounded-full"
                          :class="{
                            'bg-green-100 text-green-800': record.status === 'present',
                            'bg-red-100 text-red-800': record.status === 'absent',
                            'bg-yellow-100 text-yellow-800': record.status === 'late'
                          }">
                          {{ record.status }}
                        </span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">{{ record.checkIn || 'N/A' }}</td>
                      <td class="px-6 py-4 whitespace-nowrap">{{ record.checkOut || 'N/A' }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ record.notes || 'N/A' }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button @click="viewAttendanceDetails(record)" class="text-[#76ba51] hover:text-[#76ba51]/70 mr-2">View</button>
                        <button @click="correctRecord(record)" class="text-[#76ba51] hover:text-[#76ba51]/70">Correct</button>
                      </td>
                    </tr>
                    <tr v-if="attendanceRecords.length === 0">
                      <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">
                        No attendance records found yet for {{ selectedEmployee.name }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- ✅ 7. Attendance Summary for Selected Employee (Only shows when employee selected) -->
            <div v-if="selectedEmployee" class="mb-8">
              <h3 class="text-lg font-medium mb-3">Attendance Summary</h3>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="bg-white p-4 rounded-lg border shadow-sm">
                  <p class="text-sm font-medium text-gray-500">Days Present</p>
                  <p class="text-2xl font-bold">{{ attendanceSummary.present }}</p>
                  <p class="mt-2 text-xs text-gray-500">This month</p>
                </div>
                <div class="bg-white p-4 rounded-lg border shadow-sm">
                  <p class="text-sm font-medium text-gray-500">Days Absent</p>
                  <p class="text-2xl font-bold">{{ attendanceSummary.absent }}</p>
                  <p class="mt-2 text-xs text-gray-500">This month</p>
                </div>
                <div class="bg-white p-4 rounded-lg border shadow-sm">
                  <p class="text-sm font-medium text-gray-500">Late Arrivals</p>
                  <p class="text-2xl font-bold">{{ attendanceSummary.late }}</p>
                  <p class="mt-2 text-xs text-gray-500">This month</p>
                </div>
              </div>
            </div>

          </div>

          <!-- ✅ NEW ATTENDANCE MODAL - FULLY RESTORED WITH save_attendance.php LOGIC -->
          <div v-if="showNewAttendanceModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl">
              <div class="px-6 py-4 border-b">
                <h3 class="text-lg font-medium text-gray-900">New Attendance Record</h3>
              </div>
              <div class="px-6 py-6">
                <div class="space-y-5">
                  <!-- Date -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input 
                      v-model="newAttendance.date" 
                      type="date" 
                      required 
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#76ba51] focus:border-[#76ba51]">
                  </div>
                  <!-- Status -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select 
                      v-model="newAttendance.status" 
                      @change="updateStatusId"
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#76ba51] focus:border-[#76ba51]">
                      <option v-for="status in statuses" :key="status.id" :value="status.name">
                        {{ status.name }}
                      </option>
                    </select>
                  </div>
                  <!-- Check-In Time -->
                  <div v-if="requiresCheckIn(newAttendance.status)">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Check-In Time</label>
                    <input 
                      v-model="newAttendance.checkIn" 
                      type="time" 
                      :required="requiresCheckIn(newAttendance.status)"
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#76ba51] focus:border-[#76ba51]">
                  </div>
                  <!-- Check-Out Time -->
                  <div v-if="requiresCheckIn(newAttendance.status)">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Check-Out Time</label>
                    <input 
                      v-model="newAttendance.checkOut" 
                      type="time" 
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#76ba51] focus:border-[#76ba51]">
                  </div>
                  <!-- Notes -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea 
                      v-model="newAttendance.notes" 
                      rows="3" 
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#76ba51] focus:border-[#76ba51]" 
                      placeholder="Enter any notes...">
                    </textarea>
                  </div>
                </div>
              </div>
              <div class="px-6 py-4 border-t flex justify-end space-x-3">
                <button 
                  @click="showNewAttendanceModal = false" 
                  type="button" 
                  class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Cancel
                </button>
                <button 
                  @click="submitAttendance" 
                  type="button" 
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90">
                  Submit Record
                </button>
              </div>
            </div>
          </div>
          <!-- ✅ BATCH ATTENDANCE MODAL -->
<div
  v-if="showBatchModal"
  class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start justify-center
         p-4 z-50 overflow-y-auto"
>
  <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8">
 
    <!-- Header -->
    <div class="px-6 py-4 border-b flex justify-between items-center bg-[#035a76] rounded-t-xl">
      <div>
        <h3 class="text-lg font-semibold text-white">Batch Attendance</h3>
        <p class="text-xs text-blue-200 mt-0.5">Select a department, tick who's present — everyone else is marked absent</p>
      </div>
      <button @click="closeBatchModal" class="text-white hover:text-red-300 text-2xl font-bold leading-none">×</button>
    </div>
 
    <!-- Step 1 — Department & Date -->
    <div class="px-6 py-5 border-b bg-gray-50">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
 
        <!-- Department -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Department <span class="text-red-500">*</span></label>
          <select
            v-model="batchDepartmentCode"
            @change="loadBatchStaff"
            class="w-full border border-gray-300 rounded-md px-3 py-2
                   focus:ring-[#76ba51] focus:border-[#76ba51]"
          >
            <option value="">— Select Department —</option>
            <option
              v-for="dept in departmentObjects"
              :key="dept.code"
              :value="dept.code"
            >{{ dept.name }}</option>
          </select>
        </div>
 
        <!-- Date -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Date <span class="text-red-500">*</span></label>
          <input
            v-model="batchDate"
            type="date"
            class="w-full border border-gray-300 rounded-md px-3 py-2
                   focus:ring-[#76ba51] focus:border-[#76ba51]"
          />
        </div>
 
        <!-- Default Check-in / Check-out (applied to all present staff) -->
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Default Check-In</label>
            <input
              v-model="batchDefaultCheckIn"
              type="time"
              class="w-full border border-gray-300 rounded-md px-3 py-2
                     focus:ring-[#76ba51] focus:border-[#76ba51]"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Default Check-Out</label>
            <input
              v-model="batchDefaultCheckOut"
              type="time"
              class="w-full border border-gray-300 rounded-md px-3 py-2
                     focus:ring-[#76ba51] focus:border-[#76ba51]"
            />
          </div>
        </div>
      </div>
    </div>
 
    <!-- Step 2 — Staff List -->
    <div v-if="batchStaff.length > 0" class="px-6 py-4">
 
      <!-- Toolbar: select-all + search + counters -->
      <div class="flex flex-wrap justify-between items-center mb-3 gap-3">
        <div class="flex items-center gap-4">
          <label class="flex items-center gap-2 cursor-pointer select-none text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              :checked="allBatchChecked"
              :indeterminate.prop="someBatchChecked"
              @change="toggleAllBatch"
              class="w-4 h-4 accent-[#76ba51]"
            />
            Select All
          </label>
          <!-- Quick counters -->
          <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
            ✓ {{ batchPresentCount }} Present
          </span>
          <span class="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">
            ✗ {{ batchStaff.length - batchPresentCount }} Absent
          </span>
        </div>
 
        <!-- Search within staff list -->
        <input
          v-model="batchStaffSearch"
          type="text"
          placeholder="Filter staff…"
          class="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-56
                 focus:ring-[#76ba51] focus:border-[#76ba51]"
        />
      </div>
 
      <!-- Staff table -->
      <div class="overflow-y-auto max-h-96 rounded-lg border">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10">Present</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff ID</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-In</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="staff in filteredBatchStaff"
              :key="staff.applicant_id"
              :class="staff.isPresent ? 'bg-green-50' : 'bg-white'"
              class="hover:bg-gray-50 transition-colors"
            >
              <!-- Checkbox -->
              <td class="px-4 py-3">
                <input
                  type="checkbox"
                  v-model="staff.isPresent"
                  @change="onBatchCheckChange(staff)"
                  class="w-4 h-4 accent-[#76ba51]"
                />
              </td>
 
              <!-- Name -->
              <td class="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                {{ staff.name }}
              </td>
 
              <!-- Staff ID -->
              <td class="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                {{ staff.id }}
              </td>
 
              <!-- Position -->
              <td class="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                {{ staff.position }}
              </td>
 
              <!-- Status badge (live preview) -->
              <td class="px-4 py-3">
                <span v-if="staff.isPresent">
                  <select
                    v-model="staff.statusId"
                    class="text-xs border rounded px-2 py-1 focus:ring-[#76ba51] focus:border-[#76ba51]"
                  >
                    <option
                      v-for="s in presentStatuses"
                      :key="s.id"
                      :value="s.id"
                    >{{ s.name }}</option>
                  </select>
                </span>
                <span v-else class="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                  Absent
                </span>
              </td>
 
              <!-- Individual check-in override -->
              <td class="px-4 py-3">
                <input
                  v-if="staff.isPresent"
                  v-model="staff.checkIn"
                  type="time"
                  class="text-xs border rounded px-2 py-1 w-28
                         focus:ring-[#76ba51] focus:border-[#76ba51]"
                />
                <span v-else class="text-xs text-gray-400">—</span>
              </td>
            </tr>
 
            <tr v-if="filteredBatchStaff.length === 0">
              <td colspan="6" class="px-6 py-6 text-center text-sm text-gray-500">
                No staff found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
 
    <!-- Empty state -->
    <div v-else-if="batchDepartmentCode && !batchLoading" class="px-6 py-10 text-center text-gray-500 text-sm">
      No staff found for the selected department.
    </div>
    <div v-else-if="batchLoading" class="px-6 py-10 text-center text-gray-500 text-sm">
      Loading staff…
    </div>
    <div v-else class="px-6 py-10 text-center text-gray-400 text-sm italic">
      Select a department above to load staff
    </div>
 
    <!-- Footer -->
    <div class="px-6 py-4 border-t flex justify-between items-center bg-gray-50 rounded-b-xl">
      <p class="text-xs text-gray-500">
        Staff without a tick will be <strong>automatically marked absent</strong>.
        Already-existing records for this date are skipped.
      </p>
      <div class="flex gap-3">
        <button
          @click="closeBatchModal"
          class="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          @click="submitBatchAttendance"
          :disabled="batchStaff.length === 0 || batchSubmitting"
          class="inline-flex items-center px-5 py-2 border border-transparent text-sm
                 font-medium rounded-md shadow-sm text-white bg-[#76ba51]
                 hover:bg-[#76ba51]/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="batchSubmitting">Saving…</span>
          <span v-else>Submit Batch ({{ batchStaff.length }} staff)</span>
        </button>
      </div>
    </div>
 
  </div>
</div>
          <!-- Attendance Details Modal (unchanged) -->
          <div v-if="selectedAttendance" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl">
              <div class="px-6 py-4 border-b flex justify-between items-center">
                <h3 class="text-lg font-medium text-gray-900">Attendance Details - {{ formatDate(selectedAttendance.date) }}</h3>
                <button @click="selectedAttendance = null" class="text-gray-400 hover:text-gray-500">
                  <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="px-6 py-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-4">
                    <div><p class="text-sm text-gray-500">Employee</p><p class="font-medium">{{ selectedAttendance.employeeName }}</p></div>
                    <div><p class="text-sm text-gray-500">Date</p><p class="font-medium">{{ formatDate(selectedAttendance.date) }}</p></div>
                    <div><p class="text-sm text-gray-500">Status</p>
                      <span class="px-2 py-1 text-xs rounded-full" :class="{'bg-green-100 text-green-800': selectedAttendance.status === 'present', 'bg-red-100 text-red-800': selectedAttendance.status === 'absent', 'bg-yellow-100 text-yellow-800': selectedAttendance.status === 'late'}">
                        {{ selectedAttendance.status }}
                      </span>
                    </div>
                  </div>
                  <div class="space-y-4">
                    <div><p class="text-sm text-gray-500">Check-In Time</p><p class="font-medium">{{ selectedAttendance.checkIn || 'N/A' }}</p></div>
                    <div><p class="text-sm text-gray-500">Check-Out Time</p><p class="font-medium">{{ selectedAttendance.checkOut || 'N/A' }}</p></div>
                  </div>
                </div>
                <div v-if="selectedAttendance.notes" class="mt-6">
                  <p class="text-sm text-gray-500">Notes</p>
                  <p class="mt-1 p-3 bg-gray-50 rounded">{{ selectedAttendance.notes }}</p>
                </div>
              </div>
              <div v-if="isManager" class="px-6 py-4 border-t flex justify-end space-x-3">
                <button @click="correctRecord(selectedAttendance)" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#76ba51]">Correct Record</button>
              </div>
            </div>
          </div>
          
        </div>
      </main>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import api from "@/services/api";
import HrSidebar from '../../components/hr/HrSidebar.vue';
import HrTopbar from '../../components/hr/HrTopbar.vue';

// ==================== Reactive State ====================
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);
const currentPageTitle = ref('Attendance Management');
const isManager = ref(true);

const employees = ref([]);
const departments = ref([]);
const selectedEmployee = ref(null);

// Organization-wide attendance
const allAttendanceRecords = ref([]);
const dailyAttendanceLoading = ref(false);

// Employee-specific attendance
const attendanceRecords = ref([]);          
const employeeAttendanceLoading = ref(false);

const showNewAttendanceModal = ref(false);
const selectedAttendance = ref(null);
const showDetailedView = ref(false);

const employeeSearch = ref('');
const searchResults = ref([]);
const statuses = ref([]);

const syncingBiometric = ref(false);
const pullingBiometric = ref(false);

// New Attendance Form
const newAttendance = ref({
  id: null,
  date: new Date().toISOString().split('T')[0],
  status: 'present',
  status_id: null,
  checkIn: '',
  checkOut: '',
  notes: '',
});

// Summary Cards
const totalRecords = ref(0);
const presentToday = ref(0);
const absentToday = ref(0);
const lateToday = ref(0);

// Daily Attendance Filters
const dailyFilterFromDate = ref('');
const dailyFilterToDate = ref('');
const dailyFilterStatus = ref('all');
const dailyFilterDepartment = ref('all');
const dailyFilterSection = ref('all');

// Employee-Specific Filters
const filterFromDate = ref('');
const filterToDate = ref('');
const filterStatus = ref('all');

// Employee Attendance Summary
const attendanceSummary = ref({ present: 0, absent: 0, late: 0 });

// ==================== Computed Properties ====================
const filteredDailyAttendance = computed(() => {
  let filtered = [...allAttendanceRecords.value];
  if (dailyFilterFromDate.value) filtered = filtered.filter(r => r.date >= dailyFilterFromDate.value);
  if (dailyFilterToDate.value) filtered = filtered.filter(r => r.date <= dailyFilterToDate.value);
  if (dailyFilterStatus.value !== 'all') filtered = filtered.filter(r => r.status === dailyFilterStatus.value);
  if (dailyFilterDepartment.value !== 'all') filtered = filtered.filter(r => r.department === dailyFilterDepartment.value);
  return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
});

const departmentObjects = computed(() => {
  const map = new Map();
  employees.value.forEach(emp => {
    if (emp.department_code && emp.department && emp.department !== 'N/A') {
      map.set(emp.department_code, emp.department);
    }
  });
  return Array.from(map.entries())
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

const presentStatuses = computed(() =>
  statuses.value.filter(s => !s.name.toLowerCase().includes('absent'))
);

const absentStatusId = computed(() => {
  const found = statuses.value.find(s => s.name.toLowerCase().includes('absent'));
  return found?.id ?? null;
});

// ==================== Fetch Data on Mount ====================
onMounted(async () => {
  // 1. Fetch Employees
  try {
    const res = await api.get('get_hired_applicants.php');
    if (res.data.success) {
      employees.value = res.data.data.map(emp => ({
        id: emp.employee_code || `APP-${emp.applicant_id}`,
        applicant_id: emp.applicant_id,
        employee_id: emp.employee_id,
        employee_code: emp.employee_code,
        name: emp.full_name || `Applicant #${emp.applicant_id}`,
        position: emp.position?.position_title || 'N/A',
        department: emp.department?.department_name || 'N/A',
        department_code: emp.department?.department_code || null,
        section: emp.section?.section_name?.toLowerCase() || 'non-teaching',
      }));
      
      const deptSet = new Set(employees.value.map(e => e.department).filter(d => d && d !== 'N/A'));
      departments.value = Array.from(deptSet).sort();
    }
  } catch (err) {
    console.error('Error fetching employees:', err);
  }

  // 2. Fetch Statuses
  try {
    const response = await api.get("AttendanceStatus.php");
    statuses.value = response.data.map(status => ({
      id: status.id,
      name: status.name,
      code: status.code,
      color: status.color,
      description: status.description
    }));
  } catch (error) {
    console.error("Error fetching statuses:", error);
  }
  
  // 3. Fetch organization-wide attendance
  fetchDailyAttendance();
});

// ==================== Fetch Organization-Wide Daily Attendance ====================
const fetchDailyAttendance = async () => {
  dailyAttendanceLoading.value = true;
  try {
    const params = new URLSearchParams();
    if (dailyFilterFromDate.value) params.append('from_date', dailyFilterFromDate.value);
    if (dailyFilterToDate.value) params.append('to_date', dailyFilterToDate.value);
    if (dailyFilterStatus.value !== 'all') params.append('status', dailyFilterStatus.value);
    if (dailyFilterDepartment.value !== 'all') params.append('department', dailyFilterDepartment.value);
    if (dailyFilterSection.value !== 'all') params.append('section', dailyFilterSection.value);
    
    const response = await api.get(`get_all_attendances.php?${params.toString()}`);
    
    if (response.data.status === "success") {
      allAttendanceRecords.value = response.data.data.map(record => ({
        id: record.id,
        applicant_id: record.applicant_id,
        
        // ✅ EXACT MATCH WITH BACKEND RESPONSE
        employeeName: record.full_name || `Applicant #${record.applicant_id}`,
        employeeId: record.employee_code || `APP-${record.applicant_id}`,
        department: record.department?.department_name || 'N/A',
        position: record.position?.position_title || 'N/A',
        
        // Attendance data
        date: record.attendance_date,
        status: (record.status_name || 'unknown').toLowerCase().trim(),
        statusLabel: record.status_name || 'Unknown',
        color: record.color || null,
        checkIn: record.check_in ? record.check_in.substring(11, 16) : null,
        checkOut: record.check_out ? record.check_out.substring(11, 16) : null,
        workHours: record.work_hours,
        overtimeHours: record.overtime_hours,
        lateMinutes: record.late_minutes,
        notes: record.notes,
        attendance_type: record.attendance_type,
      }));

      // Update summary cards based on today's records
      const today = new Date().toISOString().split('T')[0];
      const todayRecords = allAttendanceRecords.value.filter(r => r.date === today);
      totalRecords.value = allAttendanceRecords.value.length;
      presentToday.value = todayRecords.filter(r => r.status === 'present').length;
      absentToday.value = todayRecords.filter(r => r.status === 'absent').length;
      lateToday.value = todayRecords.filter(r => r.status === 'late').length;
    }
  } catch (error) {
    console.error("Error fetching daily attendance:", error);
    allAttendanceRecords.value = [];
  } finally {
    dailyAttendanceLoading.value = false;
  }
};

// ==================== Fetch Attendance for Selected Employee ====================
const fetchAttendance = async () => {
  if (!selectedEmployee.value?.applicant_id) return;
  employeeAttendanceLoading.value = true;

  try {
    const params = new URLSearchParams({ 
      applicant_id: selectedEmployee.value.applicant_id 
    });
    
    if (filterFromDate.value) params.append('from_date', filterFromDate.value);
    if (filterToDate.value) params.append('to_date', filterToDate.value);
    if (filterStatus.value && filterStatus.value !== 'all') {
      params.append('status_name', filterStatus.value.toLowerCase());
    }

    const response = await api.get(`get_attendance.php?${params.toString()}`);

    if (response.data.status === "success" || response.data.success) {
      const rawData = response.data.data || response.data.records || [];
      
      attendanceRecords.value = rawData.map(record => {
        return {
          id: record.id,
          applicant_id: record.applicant_id,
          // ✅ Use full_name from backend, fallback to selected employee name
          employeeName: record.full_name?.trim() || selectedEmployee.value.name,
          // ✅ Use employee_code from backend, fallback to applicant ID
          employeeId: record.employee_code || selectedEmployee.value.id,
          date: record.attendance_date,
          status: (record.status_name || record.status || 'unknown').toLowerCase().trim(),
          status_code: record.status_code || null,
          color: record.color,
          checkIn: record.check_in ? record.check_in.substring(11, 16) : null,
          checkOut: record.check_out ? record.check_out.substring(11, 16) : null,
          workHours: record.work_hours,
          overtimeHours: record.overtime_hours,
          lateMinutes: record.late_minutes,
          notes: record.notes,
          attendance_type: record.attendance_type,
        };
      });
      
      updateAttendanceSummary();
    }
  } catch (error) {
    console.error("Error fetching attendance:", error);
    attendanceRecords.value = [];
    attendanceSummary.value = { present: 0, absent: 0, late: 0 };
  } finally {
    employeeAttendanceLoading.value = false;
  }
};

const updateAttendanceSummary = () => {
  const summary = { present: 0, absent: 0, late: 0 };
  attendanceRecords.value.forEach(record => {
    if (record.status === 'present') summary.present++;
    else if (record.status === 'absent') summary.absent++;
    else if (record.status === 'late') summary.late++;
  });
  attendanceSummary.value = summary;
};

// ==================== Employee Search ====================
const searchEmployees = () => {
  if (employeeSearch.value.length < 2) { searchResults.value = []; return; }
  const term = employeeSearch.value.toLowerCase();
  searchResults.value = employees.value.filter(emp =>
    emp.name.toLowerCase().includes(term) || emp.id.toLowerCase().includes(term)
  );
};

const selectEmployee = (employee) => {
  selectedEmployee.value = employee;
  employeeSearch.value = employee.name;
  searchResults.value = [];
  attendanceSummary.value = { present: 0, absent: 0, late: 0 };
  fetchAttendance();
};

const clearSelectedEmployee = () => {
  selectedEmployee.value = null;
  employeeSearch.value = '';
  attendanceRecords.value = [];
};

// ==================== Filter Reset ====================
const resetDailyFilters = () => {
  dailyFilterFromDate.value = '';
  dailyFilterToDate.value = '';
  dailyFilterStatus.value = 'all';
  dailyFilterDepartment.value = 'all';
  fetchDailyAttendance();
};

const resetFilters = () => {
  filterFromDate.value = '';
  filterToDate.value = '';
  filterStatus.value = 'all';
  fetchAttendance();
};

// ==================== New Attendance Modal ====================
const requiresCheckIn = (statusName) => {
  if (!statusName) return false;
  const name = statusName.toLowerCase();
  return name.includes('present') || name.includes('late') || name.includes('on time');
};

const openNewAttendanceModal = () => {
  if (!selectedEmployee.value) {
    alert('Please select an employee first');
    return;
  }
  const defaultStatus = statuses.value.find(s => s.name.toLowerCase() === 'present') || statuses.value[0];
  newAttendance.value = {
    id: null,
    date: new Date().toISOString().split('T')[0],
    status: defaultStatus?.name || 'present',
    status_id: defaultStatus?.id || null,
    checkIn: '',
    checkOut: '',
    notes: '',
  };
  showNewAttendanceModal.value = true;
};

const updateStatusId = () => {
  const found = statuses.value.find(s => s.name.toLowerCase() === newAttendance.value.status.toLowerCase());
  newAttendance.value.status_id = found ? found.id : null;
};

const submitAttendance = async () => {
  if (!selectedEmployee.value?.applicant_id) return alert('Please select an employee first');
  if (!newAttendance.value.date) return alert('Please select a date');
  if ((newAttendance.value.status === 'present' || newAttendance.value.status === 'late') && !newAttendance.value.checkIn) {
    return alert('Please provide a check-in time');
  }

  let workHours = null, overtimeHours = 0, lateMinutes = 0, earlyLeaveMinutes = 0;
  const checkIn = newAttendance.value.checkIn;
  const checkOut = newAttendance.value.checkOut;

  if (checkIn && checkOut) {
    const checkInTime = new Date(`2000-01-01 ${checkIn}`);
    const checkOutTime = new Date(`2000-01-01 ${checkOut}`);
    const diffMs = checkOutTime - checkInTime;
    workHours = parseFloat((diffMs / (1000 * 60 * 60)).toFixed(2));
    if (workHours > 8.0) overtimeHours = parseFloat((workHours - 8.0).toFixed(2));
  }

  let expectedStartTime = "08:00";
  if (selectedEmployee.value.position) {
    const title = selectedEmployee.value.position.toLowerCase();
    if (title.includes("teacher") || title.includes("teach")) expectedStartTime = "07:00";
  }

  if (checkIn) {
    const checkInTime = new Date(`2000-01-01 ${checkIn}`);
    const expectedStart = new Date(`2000-01-01 ${expectedStartTime}`);
    if (checkInTime > expectedStart) {
      const diffMs = checkInTime - expectedStart;
      lateMinutes = Math.floor(diffMs / (1000 * 60));
    }
  }

  const payload = {
    applicant_id: selectedEmployee.value.applicant_id,
    attendance_date: newAttendance.value.date,
    check_in: checkIn || null,
    check_out: checkOut || null,
    work_hours: workHours,
    overtime_hours: overtimeHours,
    late_minutes: lateMinutes,
    early_leave_minutes: earlyLeaveMinutes,
    status_id: newAttendance.value.status_id,
    attendance_type: 'manual',
    updated_by: 1,
    notes: newAttendance.value.notes || null,
  };

  try {
    let response;
    if (newAttendance.value.id) {
      payload.id = newAttendance.value.id;
      response = await api.put('save_attendance.php', payload);
    } else {
      response = await api.post('save_attendance.php', payload);
    }

    if (response.data.status === "success") {
      alert(newAttendance.value.id ? 'Attendance updated successfully!' : 'Attendance saved successfully!');
      
      const newRecord = {
        id: response.data.id || newAttendance.value.id || Date.now(),
        employeeName: selectedEmployee.value.name,
        employeeId: selectedEmployee.value.id,
        department: selectedEmployee.value.department,
        date: newAttendance.value.date,
        status: newAttendance.value.status,
        checkIn: checkIn,
        checkOut: checkOut,
        notes: newAttendance.value.notes,
      };

      if (newAttendance.value.id) {
        const idx1 = attendanceRecords.value.findIndex(r => r.id === newAttendance.value.id);
        if (idx1 !== -1) attendanceRecords.value[idx1] = newRecord;
        const idx2 = allAttendanceRecords.value.findIndex(r => r.id === newAttendance.value.id);
        if (idx2 !== -1) allAttendanceRecords.value[idx2] = newRecord;
      } else {
        attendanceRecords.value.unshift(newRecord);
        allAttendanceRecords.value.unshift(newRecord);
      }

      showNewAttendanceModal.value = false;
      resetNewAttendanceForm();
      fetchDailyAttendance();
      if (selectedEmployee.value) fetchAttendance();
    } else {
      alert(response.data.message || 'Failed to save attendance');
    }
  } catch (error) {
    console.error('Attendance error:', error);
    alert('Error saving attendance. Please try again.');
  }
};

const resetNewAttendanceForm = () => {
  newAttendance.value = {
    id: null,
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    status_id: null,
    checkIn: '',
    checkOut: '',
    notes: '',
  };
};

// ==================== Biometric Sync ====================
const syncEmployeesToBiometric = async () => {
  const codes = [...new Set(employees.value.map(e => e.employee_code).filter(Boolean))];
  if (codes.length === 0) return alert('No employees loaded to sync');
  if (!confirm(`Send ${codes.length} employees to the biometric device?`)) return;

  syncingBiometric.value = true;
  try {
    const response = await api.post('sync_biometric_users.php', { employee_codes: codes });
    const res = response.data;
    if (res.success) {
      const msg = [
        `✅ Biometric sync complete`,
        `• Synced: ${res.synced}`,
        res.failed > 0 ? `• Failed: ${res.failed}` : null,
        res.errors?.length > 0 ? `• Errors:\n${res.errors.join('\n')}` : null,
      ].filter(Boolean).join('\n');
      alert(msg);
    } else {
      alert(res.message || 'Biometric sync failed');
    }
  } catch (error) {
    console.error('Biometric sync error:', error);
    alert('Error syncing to the biometric device. Please try again.');
  } finally {
    syncingBiometric.value = false;
  }
};

const pullBiometricAttendance = async () => {
  pullingBiometric.value = true;
  try {
    const response = await api.post('pull_biometric_attendance.php', {});
    const res = response.data;
    if (res.success) {
      const msg = [
        `✅ Pulled attendance from device`,
        `• Inserted: ${res.inserted}`,
        `• Updated: ${res.updated}`,
        res.unmatched_uids?.length > 0 ? `• Unrecognized device UIDs: ${res.unmatched_uids.join(', ')}` : null,
      ].filter(Boolean).join('\n');
      alert(msg);
      fetchDailyAttendance();
    } else {
      alert(res.message || 'Pull failed');
    }
  } catch (error) {
    console.error('Biometric pull error:', error);
    alert('Error pulling attendance from the device. Please try again.');
  } finally {
    pullingBiometric.value = false;
  }
};

// ==================== Helper Functions ====================
const viewAttendanceDetails = (record) => {
  selectedAttendance.value = record;
};

const correctRecord = (record) => {
  if (!selectedEmployee.value && record.employeeId) {
    const emp = employees.value.find(e => e.id === record.employeeId);
    if (emp) selectedEmployee.value = emp;
  }
  
  newAttendance.value = {
    id: record.id,
    date: record.date,
    status: record.status,
    status_id: null,
    checkIn: record.checkIn || '',
    checkOut: record.checkOut || '',
    notes: record.notes || '',
  };
  const found = statuses.value.find(s => s.name.toLowerCase() === record.status.toLowerCase());
  if (found) newAttendance.value.status_id = found.id;
  showNewAttendanceModal.value = true;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ==================== Batch Attendance State ====================
const showBatchModal = ref(false);
const batchDepartmentCode = ref('');
const batchDate = ref(new Date().toISOString().split('T')[0]);
const batchDefaultCheckIn = ref('07:00');
const batchDefaultCheckOut = ref('15:00');
const batchStaff = ref([]);
const batchStaffSearch = ref('');
const batchLoading = ref(false);
const batchSubmitting = ref(false);

const filteredBatchStaff = computed(() => {
  if (!batchStaffSearch.value) return batchStaff.value;
  const term = batchStaffSearch.value.toLowerCase();
  return batchStaff.value.filter(s =>
    s.name.toLowerCase().includes(term) || s.id.toLowerCase().includes(term)
  );
});

const batchPresentCount = computed(() => batchStaff.value.filter(s => s.isPresent).length);
const allBatchChecked = computed(() => batchStaff.value.length > 0 && batchPresentCount.value === batchStaff.value.length);
const someBatchChecked = computed(() => batchPresentCount.value > 0 && batchPresentCount.value < batchStaff.value.length);

const toggleAllBatch = (e) => {
  const checked = e.target.checked;
  batchStaff.value.forEach(s => {
    s.isPresent = checked;
    if (checked && !s.checkIn) s.checkIn = batchDefaultCheckIn.value;
  });
};

const onBatchCheckChange = (staff) => {
  if (staff.isPresent && !staff.checkIn) {
    staff.checkIn = batchDefaultCheckIn.value;
  }
};

const openBatchModal = () => {
  batchDepartmentCode.value = '';
  batchDate.value = new Date().toISOString().split('T')[0];
  batchDefaultCheckIn.value = '07:00';
  batchDefaultCheckOut.value = '15:00';
  batchStaff.value = [];
  batchStaffSearch.value = '';
  showBatchModal.value = true;
};

const closeBatchModal = () => {
  showBatchModal.value = false;
};

const loadBatchStaff = () => {
  if (!batchDepartmentCode.value) { batchStaff.value = []; return; }
  batchLoading.value = true;

  const defaultPresentStatusId = presentStatuses.value[0]?.id ?? null;

  batchStaff.value = employees.value
    .filter(emp => emp.department_code === batchDepartmentCode.value)
    .map(emp => ({
      applicant_id: emp.applicant_id,
      name: emp.name,
      id: emp.id,
      position: emp.position,
      isPresent: false,
      statusId: defaultPresentStatusId,
      checkIn: '',
    }));

  batchLoading.value = false;
};

const submitBatchAttendance = async () => {
  if (!batchDepartmentCode.value) return alert('Please select a department');
  if (!batchDate.value) return alert('Please select a date');
  if (!absentStatusId.value) return alert('Could not find an "Absent" status. Check your statuses setup.');
  if (batchStaff.value.length === 0) return alert('No staff loaded');

  batchSubmitting.value = true;

  const presentRecords = batchStaff.value
    .filter(s => s.isPresent)
    .map(s => {
      const checkIn = s.checkIn || batchDefaultCheckIn.value || null;
      const checkOut = batchDefaultCheckOut.value || null;

      let workHours = null;
      let overtimeHours = 0;
      let lateMinutes = 0;

      if (checkIn && checkOut) {
        const inTime = new Date(`2000-01-01 ${checkIn}`);
        const outTime = new Date(`2000-01-01 ${checkOut}`);
        workHours = parseFloat(((outTime - inTime) / 3_600_000).toFixed(2));
        if (workHours > 8) overtimeHours = parseFloat((workHours - 8).toFixed(2));
      }

      if (checkIn) {
        const inTime = new Date(`2000-01-01 ${checkIn}`);
        const expected = new Date('2000-01-01 08:00');
        if (inTime > expected) {
          lateMinutes = Math.floor((inTime - expected) / 60_000);
        }
      }

      return {
        applicant_id: s.applicant_id,
        status_id: s.statusId,
        check_in: checkIn,
        check_out: checkOut,
        work_hours: workHours,
        overtime_hours: overtimeHours,
        late_minutes: lateMinutes,
        notes: null,
      };
    });

  const payload = {
    attendance_date: batchDate.value,
    department_code: batchDepartmentCode.value,
    attendance_type: 'manual',
    updated_by: 1,
    absent_status_id: absentStatusId.value,
    records: presentRecords,
  };

  try {
    const response = await api.post('save_batch_attendance.php', payload);
    const res = response.data;

    if (res.status === 'success') {
      const msg = [
        `✅ Batch saved successfully!`,
        `• Inserted: ${res.inserted}`,
        `• Auto-marked absent: ${res.absent_marked}`,
        res.skipped > 0 ? `• Skipped (already existed): ${res.skipped}` : null,
        res.errors?.length > 0 ? `• Errors: ${res.errors.length}` : null,
      ].filter(Boolean).join('\n');

      alert(msg);
      closeBatchModal();
      fetchDailyAttendance();
    } else {
      alert(res.message || 'Batch save failed');
    }
  } catch (err) {
    console.error('Batch attendance error:', err);
    alert('Error submitting batch attendance. Please try again.');
  } finally {
    batchSubmitting.value = false;
  }
};
</script>
<style scoped>
</style>